/**
 * Toast — transient notification stacked at the top of the screen.
 *
 * Used for "Player joined", "Connection lost — reconnecting…", etc.
 * Self-managing: each toast fades and removes itself.
 */
import { Theme } from './Theme.js';

export class Toast {
  /** @param {Phaser.Scene} scene */
  constructor(scene) {
    this.scene = scene;
    this._stack = [];
  }

  /**
   * @param {string} message
   * @param {object} [opts]
   * @param {number} [opts.durationMs=3000]
   * @param {'info'|'success'|'danger'} [opts.kind='info']
   */
  show(message, opts = {}) {
    const dur = opts.durationMs || 3000;
    const kind = opts.kind || 'info';
    const fill =
      kind === 'success' ? Theme.colors.success :
      kind === 'danger'  ? Theme.colors.danger  :
                           Theme.colors.surface;

    const container = this.scene.add.container(this.scene.scale.width / 2, 60);
    const text = this.scene.add.text(0, 0, message, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.md,
      color: Theme.colors.text,
      align: 'center'
    }).setOrigin(0.5);

    const padX = 20, padY = 12;
    const w = text.width + padX * 2;
    const h = text.height + padY * 2;

    const bg = this.scene.add.graphics();
    bg.fillStyle(fill, 0.92);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, Theme.radius);

    container.add([bg, text]);
    container.setDepth(2000);
    container.setAlpha(0);

    // Stack: shift older toasts down.
    for (let i = 0; i < this._stack.length; i++) {
      const c = this._stack[i];
      this.scene.tweens.add({ targets: c, y: c.y + h + 8, duration: 200 });
    }
    this._stack.unshift(container);

    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      y: 60,
      duration: 200,
      onComplete: () => {
        this.scene.time.delayedCall(dur, () => {
          this.scene.tweens.add({
            targets: container,
            alpha: 0,
            duration: 250,
            onComplete: () => {
              container.destroy();
              const idx = this._stack.indexOf(container);
              if (idx !== -1) this._stack.splice(idx, 1);
            }
          });
        });
      }
    });
  }
}
